import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import { collection, doc, getDoc, getDocs, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { validateFile } from '../utils/inputValidation';
import { MAX_DOCUMENT_SIZE_BYTES, DOCUMENT_ACCEPTED_FILE_EXTENSIONS, DOCUMENT_ACCEPTED_MIME_TYPES } from '../constants/fileUploadLimits';

const LearnerDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile, isAdmin, isFacilitator } = useAuth();
  const [learner, setLearner] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [rejectionComment, setRejectionComment] = useState('');
  const [error, setError] = useState('');

  // Document replacement state
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replacementFile, setReplacementFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  useEffect(() => {
    const fetchLearner = async () => {
      try {
        const learnerDoc = await getDoc(doc(db, 'users', id));
        if (learnerDoc.exists()) {
          setLearner({ id: learnerDoc.id, ...learnerDoc.data() });
        } else {
          setError('Learner not found');
        }
      } catch (error) {
        devError('Error fetching learner:', error);
        setError('Failed to fetch learner details');
      }
    };

    const fetchDocuments = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, `users/${id}/documents`));
        const docs = [];
        querySnapshot.forEach((docSnap) => {
          docs.push({ id: docSnap.id, ...docSnap.data() });
        });
        setDocuments(docs);
      } catch (error) {
        devError('Error fetching documents:', error);
        setError('Failed to fetch documents');
      }
    };

    if (id) {
      fetchLearner();
      fetchDocuments();
    }
  }, [id]);

  const handleVerify = async (docId) => {
    try {
      await updateDoc(doc(db, `users/${id}/documents`, docId), { status: 'verified' });
      // Refresh documents to show updated status
      const querySnapshot = await getDocs(collection(db, `users/${id}/documents`));
      const docs = [];
      querySnapshot.forEach((docSnap) => {
        docs.push({ id: docSnap.id, ...docSnap.data() });
      });
      setDocuments(docs);
    } catch (error) {
      devError('Error verifying document:', error);
      setError('Failed to verify document');
    }
  };

  const handleShowRejectModal = (doc) => {
    setSelectedDoc(doc);
    setShowModal(true);
  };

  const handleReject = async () => {
    if (!rejectionComment) {
      setError('Rejection comment cannot be empty.');
      return;
    }
    try {
      await updateDoc(doc(db, `users/${id}/documents`, selectedDoc.id), {
        status: 'rejected',
        rejectionComment,
      });
      // Refresh documents to show updated status
      const querySnapshot = await getDocs(collection(db, `users/${id}/documents`));
      const docs = [];
      querySnapshot.forEach((docSnap) => {
        docs.push({ id: docSnap.id, ...docSnap.data() });
      });
      setDocuments(docs);
      setShowModal(false);
      setRejectionComment('');
      setError('');
    } catch (error) {
      devError('Error rejecting document:', error);
      setError('Failed to reject document');
    }
  };

  const handleShowReplaceModal = (doc) => {
    setSelectedDoc(doc);
    setShowReplaceModal(true);
    setReplacementFile(null);
    setUploadError('');
    setUploadSuccess('');
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file
      const validation = validateFile(selectedFile, {
        maxSize: MAX_DOCUMENT_SIZE_BYTES,
        allowedTypes: DOCUMENT_ACCEPTED_FILE_EXTENSIONS.map((ext) => ext.replace('.', '')),
        allowedMimeTypes: Array.from(DOCUMENT_ACCEPTED_MIME_TYPES)
      });

      if (!validation.isValid) {
        setUploadError(validation.errors.join('. '));
        setReplacementFile(null);
        return;
      }

      setReplacementFile(selectedFile);
      setUploadError('');
    }
  };

  const handleReplaceDocument = async () => {
    if (!replacementFile) {
      setUploadError('Please select a file to upload.');
      return;
    }

    if (!selectedDoc) {
      setUploadError('No document selected for replacement.');
      return;
    }

    setUploading(true);
    setUploadError('');
    setUploadSuccess('');
    setUploadProgress(0);

    try {
      // Delete old file from storage if it exists
      if (selectedDoc.filePath) {
        try {
          const oldFileRef = ref(storage, selectedDoc.filePath);
          await deleteObject(oldFileRef);
        } catch (deleteError) {
          devLog('Error deleting old file (may not exist):', deleteError);
          // Continue with upload even if delete fails
        }
      }

      // Create new file name using learner info
      const getUserName = () => {
        if (learner?.firstName && learner?.lastName) {
          return `${learner.firstName}${learner.lastName}`;
        } else if (learner?.displayName) {
          return learner.displayName.replace(/\s+/g, '');
        }
        return 'User';
      };

      const sanitizeName = (name) => {
        return name.replace(/[^a-zA-Z0-9]/g, '');
      };

      const getFileExtension = (filename) => {
        const parts = filename.split('.');
        return parts.length > 1 ? parts[parts.length - 1] : 'pdf';
      };

      const userName = sanitizeName(getUserName());
      const idNumber = learner?.idNumber || 'NoID';
      const documentType = sanitizeName(selectedDoc.category.replace(/\s+/g, ''));
      const fileExtension = getFileExtension(replacementFile.name);
      const customFileName = `${userName}_${idNumber}_${documentType}.${fileExtension}`;

      // Upload new file
      const storagePath = `users/${id}/documents/${selectedDoc.id}/${customFileName}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, replacementFile);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          devError('Upload error:', error);
          setUploadError('Failed to upload file. Please try again.');
          setUploading(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // Update Firestore document metadata
            const now = Timestamp.now();
            await updateDoc(doc(db, `users/${id}/documents`, selectedDoc.id), {
              fileName: replacementFile.name,
              fileURL: downloadURL,
              filePath: storagePath,
              files: [
                {
                  name: replacementFile.name,
                  type: replacementFile.type,
                  size: replacementFile.size,
                  storagePath,
                  downloadURL,
                  uploadedAt: now
                }
              ],
              lastModified: now,
              modifiedBy: userProfile.uid,
              modifiedByName: userProfile.displayName || userProfile.email,
              status: 'pending', // Reset to pending after replacement
              rejectionComment: '' // Clear any previous rejection comments
            });

            // Refresh documents list
            const querySnapshot = await getDocs(collection(db, `users/${id}/documents`));
            const docs = [];
            querySnapshot.forEach((docSnap) => {
              docs.push({ id: docSnap.id, ...docSnap.data() });
            });
            setDocuments(docs);

            setUploadSuccess('Document replaced successfully!');
            setUploading(false);
            setUploadProgress(0);

            // Close modal after 2 seconds
            setTimeout(() => {
              setShowReplaceModal(false);
              setReplacementFile(null);
              setUploadSuccess('');
            }, 2000);
          } catch (firestoreError) {
            devError('Error updating document metadata:', firestoreError);
            setUploadError('File uploaded but failed to update metadata. Please contact support.');
            setUploading(false);
          }
        }
      );
    } catch (error) {
      devError('Replace document error:', error);
      setUploadError('Failed to replace document. Please try again.');
      setUploading(false);
    }
  };

  // Role-based access control - only admins and facilitators can access learner details
  if (!userProfile || (!isAdmin() && !isFacilitator())) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Container className="mt-4">
      <Button variant="secondary" onClick={() => navigate(isAdmin() ? '/admin/users' : '/facilitator')}>
        &larr; Back to Dashboard
      </Button>
      {learner && (
        <Row className="mt-4">
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>{learner.displayName}</Card.Title>
                <Card.Text>{learner.email}</Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      {uploadSuccess && (
        <Alert variant="success" dismissible onClose={() => setUploadSuccess('')}>
          {uploadSuccess}
        </Alert>
      )}

      <h4 className="mt-4">Documents</h4>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Category</th>
            <th>File Name</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr key={doc.id}>
              <td>{doc.category}</td>
              <td><a href={doc.fileURL} target="_blank" rel="noopener noreferrer">{doc.fileName}</a></td>
              <td>
                {doc.status}
                {doc.lastModified && (
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    Last modified by {doc.modifiedByName || 'Unknown'}
                  </div>
                )}
              </td>
              <td>
                <Button variant="success" size="sm" onClick={() => handleVerify(doc.id)} disabled={doc.status === 'verified'}>
                  Verify
                </Button>{' '}
                <Button variant="danger" size="sm" onClick={() => handleShowRejectModal(doc)} disabled={doc.status === 'rejected'}>
                  Reject
                </Button>{' '}
                <Button variant="warning" size="sm" onClick={() => handleShowReplaceModal(doc)}>
                  Replace
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reject Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group>
            <Form.Label>Rejection Comment</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectionComment}
              onChange={(e) => setRejectionComment(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleReject}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Replace Document Modal */}
      <Modal show={showReplaceModal} onHide={() => !uploading && setShowReplaceModal(false)} backdrop={uploading ? 'static' : true} keyboard={!uploading}>
        <Modal.Header closeButton={!uploading}>
          <Modal.Title>Replace Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadError && <Alert variant="danger" dismissible onClose={() => setUploadError('')}>{uploadError}</Alert>}
          {uploadSuccess && <Alert variant="success">{uploadSuccess}</Alert>}

          {selectedDoc && (
            <Alert variant="info">
              <strong>Current Document:</strong> {selectedDoc.category}
              <br />
              <strong>File:</strong> {selectedDoc.fileName}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Select New File <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              accept={DOCUMENT_ACCEPTED_FILE_EXTENSIONS.join(',')}
              disabled={uploading}
            />
            <Form.Text className="text-muted">
              Max size: {Math.round(MAX_DOCUMENT_SIZE_BYTES / (1024 * 1024))}MB.
              Allowed formats: {DOCUMENT_ACCEPTED_FILE_EXTENSIONS.join(', ')}
            </Form.Text>
          </Form.Group>

          {replacementFile && !uploading && (
            <Alert variant="success">
              Selected file: <strong>{replacementFile.name}</strong> ({(replacementFile.size / 1024).toFixed(2)} KB)
            </Alert>
          )}

          {uploading && (
            <>
              <div className="mb-2 d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Uploading... {uploadProgress.toFixed(1)}%</span>
              </div>
              <ProgressBar
                now={uploadProgress}
                label={`${uploadProgress.toFixed(1)}%`}
                variant="success"
                animated
              />
            </>
          )}

          <Alert variant="warning" className="mt-3">
            <strong>Note:</strong> Replacing this document will:
            <ul className="mb-0 mt-2">
              <li>Delete the old file from storage</li>
              <li>Upload the new file</li>
              <li>Reset the document status to "pending"</li>
              <li>Clear any previous rejection comments</li>
            </ul>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReplaceModal(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleReplaceDocument}
            disabled={!replacementFile || uploading}
          >
            {uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              'Replace Document'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LearnerDetailsPage;